import { COLORS, SingleBarChart, Text } from "@asayinc/component-library";
import { Box } from "@mui/material";
import { IProposal } from "src/store/proxyEvent/types";

const COLOR_CHOICES = [
  COLORS.DATA_VIZ_DARK_GREEN,
  COLORS.DATA_VIZ_PINK,
  COLORS.DATA_VIZ_BLUE,
  COLORS.DATA_VIZ_GREY,
  COLORS.DATA_VIZ_LIGHT_GREEN,
];

const COLOR_MAPPING: Record<string, string> = {
  for: COLORS.DATA_VIZ_DARK_GREEN,
  against: COLORS.DATA_VIZ_PINK,
  withhold: COLORS.DATA_VIZ_GREY,
  abstain: COLORS.DATA_VIZ_GREY,
  one_year: COLORS.DATA_VIZ_DARK_GREEN,
  two_years: COLORS.DATA_VIZ_BLUE,
  three_years: COLORS.DATA_VIZ_LIGHT_GREEN,
  yes: COLORS.DATA_VIZ_DARK_GREEN,
  no: COLORS.DATA_VIZ_PINK,
};

const LABEL_MAPPING: Record<string, string> = {
  for: "For",
  against: "Against",
  withhold: "Withhold",
  abstain: "Abstain",
  one_year: "One Year",
  two_years: "Two Years",
  three_years: "Three Years",
  yes: "Yes",
  no: "No",
};

// Format choice label - use mapping or convert snake_case to Title Case
const formatLabel = (choice: string): string => {
  const lowerChoice = choice.toLowerCase();
  if (LABEL_MAPPING[lowerChoice]) {
    return LABEL_MAPPING[lowerChoice];
  }
  // Fallback: convert snake_case to Title Case
  return lowerChoice
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

interface IProps {
  title?: string;
  details?: string;
  multi: boolean;
  last: boolean;
  proposal: IProposal;
  isLoading?: boolean;
}

interface ILoadingProps {
  isLoading: true;
  multi: boolean;
  last: boolean;
}

export function ProposalWithStats({
  isLoading,
  ...props
}: IProps | ILoadingProps) {
  if (isLoading) {
    return (
      <Box>
        <Text width={112} mb={3} />
        <Text width={1} mb={6} />
        <Text width={1} mb={6} />
        <Text width={1} mb={6} />
      </Box>
    );
  }

  const { title, details, multi, last, proposal } = props as IProps;
  const hasVotes = proposal.proposalChoiceStats?.some(
    (stat) => stat.voteCount > 0
  );

  // use predefined color mapping if choice is in mapping, otherwise get next
  // available color. Both scenarios, remove color from options
  const generateOptions = () => {
    const hasVotingData =
      proposal.proposalChoiceStats && proposal.proposalChoiceStats.length > 0;

    let colorChoices = [...COLOR_CHOICES];
    const getColor = (choice: string) => {
      const mappedColor = COLOR_MAPPING[choice];
      const color = mappedColor || colorChoices[0];
      colorChoices = colorChoices.filter((c) => c !== color);
      return color;
    };

    // If no voting data, create placeholder bars using available choices
    if (!hasVotingData) {
      return proposal.choices.map((choice) => ({
        value: 0,
        label: formatLabel(choice),
        color: COLORS.DATA_VIZ_GREY,
      }));
    }

    const sortedChoiceStats = [...proposal.proposalChoiceStats].sort((a, b) =>
      a.choice === "for" || b.choice === "abstain" ? -1 : 0
    );

    return sortedChoiceStats.map((choice) => ({
      value: parseFloat(choice.sharesVoted),
      label: formatLabel(choice.choice),
      color: hasVotes ? getColor(choice.choice) : COLORS.DATA_VIZ_GREY,
    }));
  };

  const options = generateOptions();
  const hasBorder = !last && multi;

  return (
    <Box>
      {title && (
        <Text variant="subtitle2" mb={multi ? 6 : 3}>
          {title}
        </Text>
      )}
      <Text variant={multi ? "subtitle2" : "body2"} mb={6}>
        {details ?? proposal.details}
      </Text>
      <Box>
        <SingleBarChart options={options} />
      </Box>
      {hasBorder && (
        <Box width="100%" bgcolor={COLORS.P_OUTLINE} height="1px" my={6} />
      )}
    </Box>
  );
}
